import { ref } from "vue";
import i18n from "@/app/plugins/i18n";

// Mirrors Vuetify's validation rule signature so rules can be passed directly
// to :rules on v-text-field / v-select / v-checkbox, etc.
type VuetifyValidationResult = string | boolean;
type VuetifyRule = (value: unknown) => VuetifyValidationResult;

export type ValidationRule = (v: string) => boolean | string;

export function useValidation() {
  const checkbox = ref(false);
  const password = ref("");
  const confirmPassword = ref("");
  const email = ref("");
  const name = ref("");
  const show = ref(false);
  const showConfirmPassword = ref(false);
  const selected = ref("");
  const company = ref("");
  const isFormValid = ref(false);
  const phone = ref("");

  const emailFormatRule = ref<ValidationRule>(
    (v) =>
      /^(([^<>()[\]\\.,;:\s@']+(\.[^<>()\\[\]\\.,;:\s@']+)*)|('.+'))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
        v,
      ) || i18n.global.t("errors.email"),
  );
  const emailRules = ref<ValidationRule[]>([
    (v) => !!v || i18n.global.t("errors.fieldRequired"),
    (v) => emailFormatRule.value(v),
  ]);
  const nameRules = ref<ValidationRule[]>([
    (v) => !!v || i18n.global.t("errors.fieldRequired"),
    (v) => (v && v.length <= 50) || i18n.global.t("errors.maxCharLimit", [50]),
    (v) => /^[a-zA-Z\s]*$/.test(v) || i18n.global.t("errors.name"),
  ]);
  const passwordRules = ref<ValidationRule[]>([
    (v) => !!v || i18n.global.t("errors.fieldRequired"),
    (v) => (v && v.length >= 8) || i18n.global.t("errors.password"),
  ]);
  const confirmPasswordRules = ref<ValidationRule[]>([
    (v) => !!v || i18n.global.t("errors.fieldRequired"),
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (v) => confirmPassword.value === password.value || i18n.global.t("errors.matchingPasswords"),
  ]);
  const checkboxRules = ref<VuetifyRule[]>([(v) => !!v || i18n.global.t("errors.agree")]);
  const selectRules = ref<VuetifyRule[]>([(v) => !!v || i18n.global.t("errors.fieldRequired")]);
  const phoneRules = ref<ValidationRule[]>([
    (v) => !!v || i18n.global.t("errors.fieldRequired"),
    (v) => /^\d+$/.test(v) || i18n.global.t("errors.phoneNumbersOnly") || true,
  ]);
  const address1 = ref("");
  const city = ref("");
  const state = ref("");
  const zip = ref("");
  const country = ref("");

  const locationRules = ref<VuetifyRule[]>([(v) => !!v || i18n.global.t("errors.required")]);

  const paymentRules = ref<ValidationRule[]>([
    (v) => !!v || i18n.global.t("errors.required"),
    (v) => (!isNaN(parseFloat(v)) && isFinite(Number(v))) || i18n.global.t("errors.number"),
    (v) => Number(v) >= 0.5 || i18n.global.t("errors.minValue", [0.5]),
    (v) => Number(v) <= 999999.99 || i18n.global.t("errors.maxValue", [999999.99]),
  ]);

  const amountFilterRules = ref<ValidationRule[]>([
    (v) => (!isNaN(parseFloat(v)) && isFinite(Number(v))) || i18n.global.t("errors.number"),
    (v) => Number(v) >= 0.5 || i18n.global.t("errors.minValue"),
    (v) => Number(v) <= 999999.99 || i18n.global.t("errors.maxValue"),
  ]);

  const maxRule = ref(
    (v: number, maxVal: number) => v <= maxVal || i18n.global.t("errors.maxValue", [maxVal]),
  );

  const numberRangeRule = ref<ValidationRule>(
    (v) =>
      (!isNaN(parseFloat(v)) && isFinite(Number(v)) && Number(v) >= 1 && Number(v) <= 99) ||
      i18n.global.t("errors.numberRange", [1, 99]),
  );

  const urlFormatRule = ref<ValidationRule>(
    (v) =>
      /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/.test(v) ||
      i18n.global.t("errors.url"),
  );
  const urlRules = ref<ValidationRule[]>([
    (v) => !!v || i18n.global.t("errors.fieldRequired"),
    (v) => urlFormatRule.value(v),
  ]);

  const paymentLinkUrlRules = ref<ValidationRule>((v) => {
    if (!v) {
      return i18n.global.t("errors.fieldRequired");
    } else if (!v.startsWith("https://")) {
      return i18n.global.t("errors.httpsRequired");
    } else {
      return true;
    }
  });

  const productQuantityRules = ref((v: number) => {
    if (v >= 1) {
      return true;
    } else {
      return i18n.global.t("errors.quantity");
    }
  });

  const customFieldCharLimit = ref((v: number) => {
    if (v) {
      return true;
    } else {
      return i18n.global.t("errors.characterLimit");
    }
  });

  const paymentLinkNameRules = ref((v: string) => {
    if (v) {
      return true;
    } else {
      return i18n.global.t("errors.nameRequired");
    }
  });

  const paymentLinkconfirmationPageRules = ref((v: string) => {
    if (v) {
      return true;
    } else {
      return i18n.global.t("errors.provideConfirmationPageUrl");
    }
  });

  const quantityRules = ref((v: number) => {
    if (v >= 1) {
      return true;
    } else {
      return i18n.global.t("errors.quantity");
    }
  });

  return {
    checkbox,
    password,
    confirmPassword,
    email,
    name,
    show,
    showConfirmPassword,
    selected,
    company,
    isFormValid,
    emailFormatRule,
    emailRules,
    nameRules,
    passwordRules,
    confirmPasswordRules,
    checkboxRules,
    selectRules,
    address1,
    city,
    state,
    zip,
    country,
    locationRules,
    paymentRules,
    amountFilterRules,
    maxRule,
    numberRangeRule,
    phone,
    phoneRules,
    urlRules,
    productQuantityRules,
    customFieldCharLimit,
    paymentLinkNameRules,
    paymentLinkconfirmationPageRules,
    quantityRules,
    paymentLinkUrlRules,
  };
}
